import { useEffect, useState } from 'react'
import { Code, Text, TextInput } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import { extractDocumentIds, formatIdList, parseIdList } from './ClassifyArgs.utils'

/**
 * Args editor/viewer for cpg.classify: { documentIds? }. Empty = sweep every document
 * currently in pending_classification (the manual-run case — followup runs chained by
 * the processing tasks carry the explicit ids of the documents they created).
 */
export function ClassifyArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const ids = extractDocumentIds(value)

  // The id list is typed free-form (commas/spaces), so keep the raw text local —
  // re-deriving it from the parsed args on every keystroke would eat separators.
  const [text, setText] = useState(() => formatIdList(ids))

  // Edit mode opened with no args: seed the empty-object default so the dialog opens
  // valid and the posted run carries explicit args.
  useEffect(() => {
    if (!readOnly && (value === undefined || value === null)) {
      onChange?.({})
    }
  }, [readOnly, value, onChange])

  if (readOnly) {
    return Array.isArray(ids) && ids.length > 0 ? (
      <Text size="sm">
        Documents: <Code>{formatIdList(ids)}</Code>
      </Text>
    ) : (
      <Text size="sm">
        <b>All pending</b> — every document in pending_classification (default)
      </Text>
    )
  }

  return (
    <TextInput
      label="Document ids"
      description="cpg_document ids to classify, separated by commas or spaces (e.g. 12, 34). Leave empty to sweep every document in pending_classification."
      placeholder="all pending documents"
      value={text}
      onChange={(event) => {
        const next = event.currentTarget.value
        setText(next)
        onChange?.(next.trim() ? { documentIds: parseIdList(next) } : {})
      }}
    />
  )
}
